import { useEffect, useMemo, useState } from "react";
import { collection, collectionGroup, onSnapshot, query, where, type DocumentData, type QueryDocumentSnapshot } from "firebase/firestore";
import { db } from "../firebase";
import { useFirm } from "../contexts/FirmContext";
import type { ExecutionStep, WorkspaceTask } from "../components/workspace/types";
import { useAppContext } from "../lib/mockState";
import { LOCAL_PREVIEW_AGENT_RUNS_EVENT, readLocalPreviewAgentRuns, USE_LOCAL_PREVIEW_SURFACE_DATA } from "../lib/localPreviewSurfaceStore";

type LiveSourceState = "live" | "empty" | "loading" | "error";

export interface LiveSurfaceResult<T> {
  records: T[];
  loading: boolean;
  error: Error | null;
  sourceState: LiveSourceState;
  lastRefreshAt: Date | null;
  sourceLabel: string;
}

export interface LiveAgentRun {
  id: string;
  title: string;
  prompt: string;
  status: WorkspaceTask["status"];
  date: string;
  matterId: string;
  matterName: string;
  executor: string;
  progress: number;
  steps: ExecutionStep[];
  hoursSaved: number;
  recoveredAED: number;
  isReported: boolean;
}

export interface LiveCalendarEvent {
  id: string;
  title: string;
  subtitle: string;
  date: Date | null;
  day: number;
  type: string;
  color: string;
  matterId: string;
  clientId: string;
  sourcePath: string;
}

export interface LiveDocumentRecord {
  id: string;
  name: string;
  category: "Matters Folders" | "Evidence Logs" | "Audited Revisions" | "Corporate KYC";
  size: string;
  contributor: string;
  dateAdded: string;
  checksum: string;
  deleted: boolean;
  legalHold: boolean;
  retentionUntil: string;
  sourcePath: string;
}

export interface LiveCommunicationRecord {
  id: string;
  clientName: string;
  sender: string;
  role: "CLIENT" | "LAWYER" | "AI_DISPATCH";
  channel: "EMAIL" | "PORTAL" | "INTERNAL_MEMO";
  date: string;
  subject: string;
  bodyEn: string;
  bodyAr: string;
  status: "READ" | "UNREAD" | "SENT_CONFIRMED" | "DELIVERY_PENDING" | "BOUNCED" | "FAILED";
  sourcePath: string;
}

export interface LiveKnowledgeCandidateRecord {
  id: string;
  title: string;
  summary: string;
  source: string;
  status: "PENDING" | "APPROVED" | "REJECTED";
  createdAt: string;
  sourcePath: string;
}

export interface LiveClientRecord {
  id: string;
  clientId: string;
  name: string;
  outstandingAR: number;
  sourcePath: string;
}

export interface LiveInvoiceRecord {
  id: string;
  clientName: string;
  matterId: string;
  issueDate: string;
  dueDate: string;
  subtotalAed: number;
  taxAed: number;
  totalAed: number;
  status: "PAID" | "PENDING" | "OVERDUE";
  sourcePath: string;
}

export interface LiveMatterSearchRecord {
  id: string;
  matterId: string;
  title: string;
  type: "CASE" | "CONTRACT";
  status: string;
  clientName: string;
  jurisdiction: string;
  sourcePath: string;
}

function asString(value: unknown, fallback = ""): string {
  return typeof value === "string" && value.trim() ? value.trim() : fallback;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}

function asBoolean(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback;
}

function asDate(value: unknown): Date | null {
  if (value instanceof Date) return value;
  if (value && typeof value === "object" && "toDate" in value && typeof (value as { toDate: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate();
  }
  if (typeof value === "string" || typeof value === "number") {
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  return null;
}

function dateLabel(value: unknown): string {
  return asDate(value)?.toISOString().substring(0, 10) ?? "";
}

function datetimeLabel(value: unknown): string {
  const date = asDate(value);
  return date ? date.toISOString().substring(0, 16).replace("T", " ") : "";
}

function normalizeStatus(value: unknown): WorkspaceTask["status"] {
  const status = asString(value, "QUEUED").toUpperCase();
  if (["COMPLETED", "FAILED", "RUNNING", "QUEUED"].includes(status)) return status as WorkspaceTask["status"];
  if (status === "COMPLETE" || status === "DONE") return "COMPLETED";
  if (status === "ERROR") return "FAILED";
  return "QUEUED";
}

function normalizeSteps(value: unknown): ExecutionStep[] {
  if (!Array.isArray(value)) return [];
  return value.map((step, index) => {
    const data = typeof step === "object" && step ? step as Record<string, unknown> : {};
    const status = asString(data.status, "pending").toLowerCase();
    return {
      id: asString(data.id, `step-${index + 1}`),
      name: asString(data.name, `Step ${index + 1}`),
      nameAr: asString(data.nameAr ?? data.name, `Step ${index + 1}`),
      status: (["pending", "running", "completed", "failed"].includes(status) ? status : "pending") as ExecutionStep["status"],
      description: asString(data.description),
      descriptionAr: asString(data.descriptionAr ?? data.description),
    };
  });
}

function readDocPath(doc: QueryDocumentSnapshot<DocumentData>): string {
  return doc.ref.path;
}

function useFirmCollectionGroup<T>(collectionId: string, normalize: (doc: QueryDocumentSnapshot<DocumentData>) => T, sourceLabel: string, enabled = true): LiveSurfaceResult<T> {
  const { firmId, firmReady } = useFirm();
  const [records, setRecords] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRecords([]);
      setLoading(false);
      setError(null);
      setLastRefreshAt(new Date());
      return;
    }

    if (!firmReady) return;
    if (!firmId) {
      setRecords([]);
      setLoading(false);
      setError(null);
      setLastRefreshAt(new Date());
      return;
    }

    setLoading(true);
    const liveQuery = query(collectionGroup(db, collectionId), where("firmId", "==", firmId));
    const unsubscribe = onSnapshot(
      liveQuery,
      (snap) => {
        setRecords(snap.docs.map(normalize));
        setError(null);
        setLoading(false);
        setLastRefreshAt(new Date());
      },
      (snapshotError) => {
        setRecords([]);
        setError(snapshotError as Error);
        setLoading(false);
        setLastRefreshAt(new Date());
      },
    );

    return unsubscribe;
  }, [collectionId, enabled, firmId, firmReady, normalize]);

  return {
    records,
    loading,
    error,
    lastRefreshAt,
    sourceLabel,
    sourceState: loading ? "loading" : error ? "error" : records.length > 0 ? "live" : "empty",
  };
}

function useFirmRootCollection<T>(collectionId: string, normalize: (doc: QueryDocumentSnapshot<DocumentData>) => T, sourceLabel: string, enabled = true): LiveSurfaceResult<T> {
  const { firmId, firmReady } = useFirm();
  const [records, setRecords] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRecords([]);
      setLoading(false);
      setError(null);
      setLastRefreshAt(new Date());
      return;
    }

    if (!firmReady) return;
    if (!firmId) {
      setRecords([]);
      setLoading(false);
      setError(null);
      setLastRefreshAt(new Date());
      return;
    }

    setLoading(true);
    const liveQuery = query(collection(db, "firms", firmId, collectionId));
    const unsubscribe = onSnapshot(
      liveQuery,
      (snap) => {
        setRecords(snap.docs.map(normalize));
        setError(null);
        setLoading(false);
        setLastRefreshAt(new Date());
      },
      (snapshotError) => {
        setRecords([]);
        setError(snapshotError as Error);
        setLoading(false);
        setLastRefreshAt(new Date());
      },
    );

    return unsubscribe;
  }, [collectionId, enabled, firmId, firmReady, normalize]);

  return {
    records,
    loading,
    error,
    lastRefreshAt,
    sourceLabel,
    sourceState: loading ? "loading" : error ? "error" : records.length > 0 ? "live" : "empty",
  };
}

function useRootFirmCollection<T>(collectionId: string, normalize: (doc: QueryDocumentSnapshot<DocumentData>) => T, sourceLabel: string, enabled = true): LiveSurfaceResult<T> {
  const { firmId, firmReady } = useFirm();
  const [records, setRecords] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastRefreshAt, setLastRefreshAt] = useState<Date | null>(null);

  useEffect(() => {
    if (!enabled) {
      setRecords([]);
      setLoading(false);
      setError(null);
      setLastRefreshAt(new Date());
      return;
    }

    if (!firmReady) return;
    if (!firmId) {
      setRecords([]);
      setLoading(false);
      setError(null);
      setLastRefreshAt(new Date());
      return;
    }

    setLoading(true);
    const liveQuery = query(collection(db, collectionId), where("firmId", "==", firmId));
    const unsubscribe = onSnapshot(
      liveQuery,
      (snap) => {
        setRecords(snap.docs.map(normalize));
        setError(null);
        setLoading(false);
        setLastRefreshAt(new Date());
      },
      (snapshotError) => {
        setRecords([]);
        setError(snapshotError as Error);
        setLoading(false);
        setLastRefreshAt(new Date());
      },
    );

    return unsubscribe;
  }, [collectionId, enabled, firmId, firmReady, normalize]);

  return {
    records,
    loading,
    error,
    lastRefreshAt,
    sourceLabel,
    sourceState: loading ? "loading" : error ? "error" : records.length > 0 ? "live" : "empty",
  };
}

export function useAgentRuns(): LiveSurfaceResult<LiveAgentRun> {
  const { matters } = useAppContext();
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveAgentRun => {
    const data = doc.data() as Record<string, unknown>;
    return {
      id: doc.id,
      title: asString(data.title ?? data.taskLabel ?? data.name, "Untitled agent run"),
      prompt: asString(data.prompt ?? data.query),
      status: normalizeStatus(data.status),
      date: asDate(data.createdAt ?? data.updatedAt ?? data.date)?.toISOString() ?? new Date(0).toISOString(),
      matterId: asString(data.matterId),
      matterName: asString(data.matterName ?? data.matterTitle, "Unlinked matter"),
      executor: asString(data.executor ?? data.agentName ?? data.createdByName, "Agent"),
      progress: asNumber(data.progress ?? data.progressPercent, normalizeStatus(data.status) === "COMPLETED" ? 100 : 0),
      steps: normalizeSteps(data.steps),
      hoursSaved: asNumber(data.hoursSaved),
      recoveredAED: asNumber(data.recoveredAED ?? data.recoveredAed ?? data.roiAed),
      isReported: asBoolean(data.isReported ?? data.reported),
    };
  }, []);
  const liveResult = useFirmRootCollection("agent_runs", normalize, "firms/{firmId}/agent_runs", !USE_LOCAL_PREVIEW_SURFACE_DATA);
  const [localPreviewRuns, setLocalPreviewRuns] = useState<WorkspaceTask[]>(() => readLocalPreviewAgentRuns());

  useEffect(() => {
    if (!USE_LOCAL_PREVIEW_SURFACE_DATA || typeof window === "undefined") return;

    const sync = () => {
      setLocalPreviewRuns(readLocalPreviewAgentRuns());
    };

    window.addEventListener(LOCAL_PREVIEW_AGENT_RUNS_EVENT, sync);
    window.addEventListener("storage", sync);
    sync();

    return () => {
      window.removeEventListener(LOCAL_PREVIEW_AGENT_RUNS_EVENT, sync);
      window.removeEventListener("storage", sync);
    };
  }, []);

  const localPreviewRecords = useMemo<LiveAgentRun[]>(() => localPreviewRuns.map((run) => ({
    id: run.id,
    title: run.title,
    prompt: run.prompt,
    status: run.status,
    date: run.date,
    matterId: run.matterId,
    matterName: run.matterName || matters.find((matter) => matter.matterId === run.matterId)?.title || "Unlinked matter",
    executor: run.executor,
    progress: run.progress,
    steps: run.steps,
    hoursSaved: run.hoursSaved,
    recoveredAED: run.recoveredAED,
    isReported: run.isReported,
  })), [localPreviewRuns, matters]);

  if (USE_LOCAL_PREVIEW_SURFACE_DATA) {
    return {
      records: localPreviewRecords,
      loading: false,
      error: null,
      lastRefreshAt: new Date(),
      sourceLabel: "local_preview/agent_runs",
      sourceState: localPreviewRecords.length > 0 ? "live" : "empty",
    };
  }

  return liveResult;
}

export function useCalendarEvents(): LiveSurfaceResult<LiveCalendarEvent> {
  const { matters, workQueue } = useAppContext();
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveCalendarEvent => {
    const data = doc.data() as Record<string, unknown>;
    const date = asDate(data.startsAt ?? data.startAt ?? data.date ?? data.dueAt);
    const type = asString(data.eventType ?? data.type, "EVENT").toUpperCase();
    const color = type.includes("HEARING") ? "red" : type.includes("DEADLINE") ? "amber" : type.includes("REMINDER") ? "orange" : "slate";
    return {
      id: doc.id,
      title: asString(data.title, "Untitled calendar event"),
      subtitle: asString(data.subtitle ?? data.matterName ?? data.matterId),
      date,
      day: date ? date.getDate() : 0,
      type,
      color,
      matterId: asString(data.matterId),
      clientId: asString(data.clientId),
      sourcePath: readDocPath(doc),
    };
  }, []);
  const liveResult = useFirmCollectionGroup("calendar_events", normalize, "firms/*/calendar_events", !USE_LOCAL_PREVIEW_SURFACE_DATA);
  const localPreviewRecords = useMemo<LiveCalendarEvent[]>(() => {
    const matterEvents = matters
      .filter((matter) => typeof matter.nextEventDate === "string" && matter.nextEventDate.trim().length > 0)
      .map((matter) => {
        const date = asDate(matter.nextEventDate);
        return {
          id: `${matter.matterId}-next-event`,
          title: matter.nextEvent || `${matter.matterSpecies} milestone`,
          subtitle: matter.title,
          date,
          day: date ? date.getDate() : 0,
          type: matter.matterSpecies === "CASE" ? "HEARING" : "REMINDER",
          color: matter.matterSpecies === "CASE" ? "red" : "orange",
          matterId: matter.matterId,
          clientId: matter.clientId,
          sourcePath: `local-preview/matters/${matter.matterId}`,
        };
      });

    const today = new Date();
    const queueEvents = workQueue.map((item, index) => {
      const offsetMatch = item.dueOrAge?.match(/in\s+(\d+)\s+days?/i);
      const offsetDays = offsetMatch ? Number(offsetMatch[1]) : item.dueOrAge?.toLowerCase().includes("today") ? 0 : index + 1;
      const date = new Date(today.getFullYear(), today.getMonth(), today.getDate() + (Number.isFinite(offsetDays) ? offsetDays : 0));
      return {
        id: `${item.id}-calendar`,
        title: item.title,
        subtitle: item.owner || item.workflow,
        date,
        day: date.getDate(),
        type: item.workflow === "HEARING" ? "HEARING" : "REMINDER",
        color: item.workflow === "HEARING" ? "red" : "orange",
        matterId: item.linkedMatterId || "",
        clientId: "",
        sourcePath: `local-preview/work-queue/${item.id}`,
      };
    });

    return [...matterEvents, ...queueEvents].filter((event, index, list) => list.findIndex((candidate) => candidate.sourcePath === event.sourcePath) === index);
  }, [matters, workQueue]);

  if (USE_LOCAL_PREVIEW_SURFACE_DATA) {
    return {
      records: localPreviewRecords,
      loading: false,
      error: null,
      lastRefreshAt: new Date(),
      sourceLabel: "local_preview/calendar_events",
      sourceState: localPreviewRecords.length > 0 ? "live" : "empty",
    };
  }

  return liveResult;
}

export function useCalendarProjectionEvents(): LiveSurfaceResult<LiveCalendarEvent> {
  const { workQueue } = useAppContext();
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveCalendarEvent => {
    const data = doc.data() as Record<string, unknown>;
    const date = asDate(data.occursAt ?? data.startsAt ?? data.date ?? data.dueAt);
    const rawType = asString(data.projectionType ?? data.occurrenceKey ?? data.eventType ?? data.type, "PROJECTION").toUpperCase();
    const sourceCollection = asString(data.sourceCollection).toUpperCase();
    const type = sourceCollection ? `${sourceCollection}_${rawType}` : rawType;
    const color = type.includes("HEARING") ? "red" : type.includes("DEADLINE") || type.includes("EXPIRY") || type.includes("DUNNING") ? "amber" : type.includes("REMINDER") || type.includes("NOTICE") ? "orange" : "slate";
    return {
      id: doc.id,
      title: asString(data.title, "Untitled calendar projection"),
      subtitle: asString(data.subtitle ?? data.matterName ?? data.matterId ?? data.sourceCollection),
      date,
      day: date ? date.getDate() : 0,
      type,
      color,
      matterId: asString(data.matterId),
      clientId: asString(data.clientId),
      sourcePath: readDocPath(doc),
    };
  }, []);
  const liveResult = useRootFirmCollection("calendar_projections", normalize, "calendar_projections", !USE_LOCAL_PREVIEW_SURFACE_DATA);
  const localPreviewRecords = useMemo<LiveCalendarEvent[]>(() => workQueue
    .filter((item) => item.title.startsWith("Reminder:"))
    .map((item, index) => ({
      id: item.id || `local-preview-reminder-${index}`,
      title: item.title,
      subtitle: item.reasonBlocked || item.workflow,
      date: null,
      day: 0,
      type: "REMINDER_PROJECTION",
      color: "orange",
      matterId: item.linkedMatterId || "",
      clientId: "",
      sourcePath: `local-preview/work-queue/${item.id || index}`,
    })), [workQueue]);

  if (USE_LOCAL_PREVIEW_SURFACE_DATA) {
    return {
      records: localPreviewRecords,
      loading: false,
      error: null,
      lastRefreshAt: new Date(),
      sourceLabel: "local_preview/calendar_projections",
      sourceState: localPreviewRecords.length > 0 ? "live" : "empty",
    };
  }

  return liveResult;
}

export function useFirmDocuments(): LiveSurfaceResult<LiveDocumentRecord> {
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveDocumentRecord => {
    const data = doc.data() as Record<string, unknown>;
    const rawCategory = asString(data.category ?? data.documentType ?? data.folder, "Matters Folders");
    const category = (["Matters Folders", "Evidence Logs", "Audited Revisions", "Corporate KYC"].includes(rawCategory) ? rawCategory : "Matters Folders") as LiveDocumentRecord["category"];
    const byteSize = asNumber(data.sizeBytes ?? data.byteSize);
    return {
      id: doc.id,
      name: asString(data.name ?? data.fileName ?? data.title, "Untitled document"),
      category,
      size: asString(data.size, byteSize > 0 ? `${(byteSize / (1024 * 1024)).toFixed(2)} MB` : "Stored"),
      contributor: asString(data.contributor ?? data.uploadedByName ?? data.createdByName, "Unknown contributor"),
      dateAdded: dateLabel(data.dateAdded ?? data.uploadedAt ?? data.createdAt),
      checksum: asString(data.checksum ?? data.sha256 ?? data.hash, "pending-server-hash"),
      deleted: data.deleted === true,
      legalHold: data.legalHold === true,
      retentionUntil: dateLabel(data.retentionUntil),
      sourcePath: readDocPath(doc),
    };
  }, []);
  return useFirmRootCollection("documents", normalize, "firms/{firmId}/documents");
}

export function useCommunications(): LiveSurfaceResult<LiveCommunicationRecord> {
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveCommunicationRecord => {
    const data = doc.data() as Record<string, unknown>;
    const role = asString(data.role ?? data.senderRole, "LAWYER").toUpperCase();
    const channel = asString(data.channel, "EMAIL").toUpperCase();
    const status = asString(data.status, "SENT_CONFIRMED").toUpperCase();
    return {
      id: doc.id,
      clientName: asString(data.clientName, "Unknown client"),
      sender: asString(data.sender ?? data.senderName ?? data.createdByName, "Unknown sender"),
      role: (["CLIENT", "LAWYER", "AI_DISPATCH"].includes(role) ? role : "LAWYER") as LiveCommunicationRecord["role"],
      channel: (["EMAIL", "PORTAL", "INTERNAL_MEMO"].includes(channel) ? channel : "EMAIL") as LiveCommunicationRecord["channel"],
      date: datetimeLabel(data.sentAt ?? data.createdAt ?? data.date),
      subject: asString(data.subject, "Untitled message"),
      bodyEn: asString(data.bodyEn ?? data.body ?? data.text),
      bodyAr: asString(data.bodyAr ?? data.body ?? data.text),
      status: (["READ", "UNREAD", "SENT_CONFIRMED", "DELIVERY_PENDING", "BOUNCED", "FAILED"].includes(status) ? status : "SENT_CONFIRMED") as LiveCommunicationRecord["status"],
      sourcePath: readDocPath(doc),
    };
  }, []);
  return useFirmRootCollection("communications", normalize, "firms/{firmId}/communications");
}

export function useKnowledgeCandidates(): LiveSurfaceResult<LiveKnowledgeCandidateRecord> {
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveKnowledgeCandidateRecord => {
    const data = doc.data() as Record<string, unknown>;
    const status = asString(data.status, "PENDING").toUpperCase();
    return {
      id: doc.id,
      title: asString(data.title ?? data.name, "Untitled candidate"),
      summary: asString(data.summary ?? data.description ?? data.body, "No summary provided."),
      source: asString(data.source ?? data.sourcePath ?? data.createdVia, "Unknown source"),
      status: (["PENDING", "APPROVED", "REJECTED"].includes(status) ? status : "PENDING") as LiveKnowledgeCandidateRecord["status"],
      createdAt: datetimeLabel(data.createdAt ?? data.updatedAt),
      sourcePath: readDocPath(doc),
    };
  }, []);
  return useFirmRootCollection("knowledge_candidates", normalize, "knowledge_candidates");
}

export function useFirmClients(): LiveSurfaceResult<LiveClientRecord> {
  const { clients } = useAppContext();
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveClientRecord => {
    const data = doc.data() as Record<string, unknown>;
    return {
      id: doc.id,
      clientId: asString(data.clientId, doc.id),
      name: asString(data.name ?? data.clientName ?? data.companyName, "Unnamed client"),
      outstandingAR: asNumber(data.outstandingAR ?? data.arValue ?? data.balanceDue),
      sourcePath: readDocPath(doc),
    };
  }, []);
  const liveResult = useFirmCollectionGroup("clients", normalize, "firms/*/clients", !USE_LOCAL_PREVIEW_SURFACE_DATA);
  const localPreviewRecords = useMemo<LiveClientRecord[]>(() => clients.map((client) => ({
    id: client.clientId,
    clientId: client.clientId,
    name: client.name,
    outstandingAR: client.outstandingAR,
    sourcePath: `local-preview/clients/${client.clientId}`,
  })), [clients]);

  if (USE_LOCAL_PREVIEW_SURFACE_DATA) {
    return {
      records: localPreviewRecords,
      loading: false,
      error: null,
      lastRefreshAt: new Date(),
      sourceLabel: "local_preview/clients",
      sourceState: localPreviewRecords.length > 0 ? "live" : "empty",
    };
  }

  return liveResult;
}

function normalizeInvoiceStatus(value: unknown): LiveInvoiceRecord["status"] {
  const status = asString(value, "PENDING").toUpperCase();
  if (["PAID", "PENDING", "OVERDUE"].includes(status)) return status as LiveInvoiceRecord["status"];
  if (["SETTLED", "CLEARED", "SUCCESS"].includes(status)) return "PAID";
  if (["LATE", "UNPAID_OVERDUE"].includes(status)) return "OVERDUE";
  return "PENDING";
}

export function useFirmInvoices(): LiveSurfaceResult<LiveInvoiceRecord> {
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveInvoiceRecord => {
    const data = doc.data() as Record<string, unknown>;
    const subtotal = asNumber(data.subtotalAed ?? data.subtotal ?? data.amountSubtotal);
    const tax = asNumber(data.taxAed ?? data.vatAed ?? data.tax);
    const total = asNumber(data.totalAed ?? data.total ?? data.amount ?? data.balanceDue, subtotal + tax);
    return {
      id: asString(data.invoiceNumber, doc.id),
      clientName: asString(data.clientName, "Unnamed client"),
      matterId: asString(data.matterId ?? data.caseId ?? data.contractId, "Unlinked"),
      issueDate: dateLabel(data.issueDate ?? data.createdAt),
      dueDate: dateLabel(data.dueDate),
      subtotalAed: subtotal,
      taxAed: tax,
      totalAed: total,
      status: normalizeInvoiceStatus(data.status),
      sourcePath: readDocPath(doc),
    };
  }, []);
  // Invoices live at the ROOT /invoices collection (firmId field), written by
  // createInvoice/approveInvoice callables and governed by root /invoices rules.
  // Must use the root-collection helper (where firmId ==) — the subcollection
  // path firms/{firmId}/invoices does not exist and is denied by rules.
  return useRootFirmCollection("invoices", normalize, "invoices");
}

export function useMatterSearchRecords(): LiveSurfaceResult<LiveMatterSearchRecord> {
  const normalize = useMemo(() => (doc: QueryDocumentSnapshot<DocumentData>): LiveMatterSearchRecord => {
    const data = doc.data() as Record<string, unknown>;
    const species = asString(data.matterSpecies ?? data.type, "CASE").toUpperCase();
    const matterId = asString(data.matterId ?? data.caseId ?? data.contractId, doc.id);
    return {
      id: doc.id,
      matterId,
      title: asString(data.title ?? data.name ?? data.matterName, "Untitled matter"),
      type: species === "CONTRACT" ? "CONTRACT" : "CASE",
      status: asString(data.status ?? data.lifecycle, "ACTIVE"),
      clientName: asString(data.clientName),
      jurisdiction: asString(data.jurisdiction, "Firm matter"),
      sourcePath: readDocPath(doc),
    };
  }, []);
  return useFirmCollectionGroup("matters", normalize, "firms/*/matters");
}
